import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Download, Gavel } from "lucide-react";
import { formatApiError } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-color.png`;
const LOGO_WHITE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-white.png`;
const STREAK_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_kpl-league/artifacts/89377fcc3ef00973_image.png";

const BID_START = "2026-11-02T09:00:00+05:30";
const BID_END = "2026-11-07T18:00:00+05:30";

const fmtIst = (ms) => new Date(ms).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });

const EMPTY_FORM = { name: "", mobile: "", email: "", city: "", message: "" };

export default function FranchiseEOI() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [appPath, setAppPath] = useState("");
  const [appUploading, setAppUploading] = useState(false);
  const [bidWindow, setBidWindow] = useState({ start: BID_START, end: BID_END });
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    axios.get(`${API}/kpl`).then((r) => {
      setBidWindow({ start: r.data?.bid_start || BID_START, end: r.data?.bid_end || BID_END });
    }).catch(() => {});
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const startMs = new Date(bidWindow.start).getTime();
  const endMs = new Date(bidWindow.end).getTime();
  const bidPhase = now < startMs ? "before" : now <= endMs ? "open" : "closed";
  const diff = Math.max(0, (bidPhase === "before" ? startMs : endMs) - now);
  const parts = [["Days", Math.floor(diff / 86400000)], ["Hours", Math.floor(diff / 3600000) % 24], ["Mins", Math.floor(diff / 60000) % 60], ["Secs", Math.floor(diff / 1000) % 60]];

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const uploadApp = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setAppUploading(true);
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await axios.post(`${API}/eoi/doc`, body);
      setAppPath(data.path);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Upload failed — try again.");
    }
    setAppUploading(false);
  };

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await axios.post(`${API}/eoi`, { ...form, application_path: appPath });
      setMsg("Thank you for your interest in owning a KPL franchise! Our team will contact you. Follow our official Instagram page for updates.");
      setForm(EMPTY_FORM);
      setAppPath("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return <div className="auth-wrap">
    <img className="auth-streak" src={STREAK_URL} alt="" aria-hidden="true" />
    <Link className="auth-home" to="/" data-testid="eoi-back-home"><ArrowLeft size={15} /> BACK TO SITE</Link>
    <div className="auth-card wide">
      <img className="auth-logo" src={LOGO_URL} alt="KPL logo" />
      <h1>Own a franchise.</h1>
      <p className="auth-sub">KPL Season One features six exciting franchises! Download the official EOI (Expression of Interest) Bidding Application or submit your interest directly using the form below.</p>
      <p className="auth-sub" data-testid="eoi-bid-dates">Franchise Bidding Opens: <b>2nd November 2026</b><br />Bidding Closing Date: <b>7th November 2026, 6:00 PM IST</b></p>
      <div className="bid-countdown" data-testid="bid-countdown">
        {bidPhase === "closed" ? <span className="countdown-label" data-testid="bid-countdown-label"><Gavel size={13} /> Bidding closed</span> : <>
          <span className={`countdown-label ${bidPhase === "open" ? "live" : ""}`} data-testid="bid-countdown-label"><Gavel size={13} /> {bidPhase === "before" ? "Bidding opens in" : "Bidding open · closes in"}</span>
          <div className="countdown-boxes">{parts.map(([label, value]) => <div className="countdown-box" key={label} data-testid={`bid-countdown-${label.toLowerCase()}`}><b>{String(value).padStart(2, "0")}</b><span>{label}</span></div>)}</div>
        </>}
        <span className="bid-dates" data-testid="bid-dates">Bids accepted: {fmtIst(startMs)} – {fmtIst(endMs)} IST</span>
      </div>
      {msg && <div className="reg-done" data-testid="eoi-success"><CheckCircle2 size={17} /> <span>{msg} <a href="https://www.instagram.com/kakinadapremierleague/" target="_blank" rel="noreferrer" data-testid="eoi-insta-link">Follow @kakinadapremierleague</a></span></div>}
      {error && <div className="auth-error" data-testid="eoi-error">{error}</div>}
      <form onSubmit={submit}>
        <div className="auth-field"><label>Your Name</label><input type="text" required minLength={2} value={form.name} onChange={(e) => setField("name", e.target.value)} placeholder="Full name" data-testid="eoi-name-input" /></div>
        <div className="auth-field"><label>Mobile Number</label><input type="tel" required minLength={10} value={form.mobile} onChange={(e) => setField("mobile", e.target.value)} placeholder="10-digit mobile number" data-testid="eoi-mobile-input" /></div>
        <div className="auth-field"><label>Email (optional)</label><input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@example.com" data-testid="eoi-email-input" /></div>
        <div className="auth-field"><label>City</label><input type="text" required minLength={2} value={form.city} onChange={(e) => setField("city", e.target.value)} placeholder="e.g. Kakinada" data-testid="eoi-city-input" /></div>
        <div className="auth-field"><label>Message (optional)</label><input type="text" value={form.message} onChange={(e) => setField("message", e.target.value)} placeholder="Anything you'd like us to know" data-testid="eoi-message-input" /></div>
        <div className="auth-field"><label>Upload your bidding application</label>
          <input type="file" accept="application/pdf" onChange={uploadApp} data-testid="eoi-application-input" />
          <small className="field-note">Download the blank application below, fill and sign it, then upload the PDF here.</small>
          {appUploading && <small className="field-note">Uploading…</small>}
          {appPath && !appUploading && <small className="field-note upload-ok" data-testid="eoi-application-ok">Application uploaded ✓</small>}
        </div>
        <button className="button button-red auth-submit" type="submit" disabled={busy || appUploading} data-testid="eoi-submit-button">{busy ? "Submitting…" : "Submit EOI Bidding Application"}</button>
      </form>
      <a className="button button-outline auth-submit auth-pdf" href={`${BACKEND_URL}/api/static/kpl-franchise-eoi.pdf`} target="_blank" rel="noreferrer" data-testid="eoi-pdf-download"><Download size={15} /> Download EOI Bidding Application</a>
      <p className="auth-sub eoi-next-note" data-testid="eoi-next-note">Once your application is submitted, our technical team will contact you to guide you through the next steps of the franchise bidding process.</p>
    </div>
  </div>;
}
